import { launchParams } from '../../api/session';
import { showImages } from './vkBridge';

/**
 * Просмотр фотографий на весь экран: решение о том, ЧЕЙ просмотрщик открыть,
 * и состояние своего.
 *
 * Развилка здесь одна на приложение, и экраны про неё не знают: `Thumb` и
 * `ImageMark` зовут `openPhotos` и всё.
 *
 * Модуль, а не контекст, — как `app/leaveGuard.ts`: открытый просмотр всегда
 * один, а контекст перерисовывал бы всё дерево ради состояния, которое нужно
 * ровно одному компоненту.
 */

export interface PhotoView {
  /** Полноразмерные адреса, абсолютные: см. `absoluteUrl` в `media.ts`. */
  images: string[];
  index: number;
}

type Listener = () => void;

let opened: PhotoView | null = null;
const listeners = new Set<Listener>();

function publish(): void {
  for (const listener of listeners) listener();
}

/**
 * Нативную галерею ВК рисует САМ клиент. В браузере это страница `vk.com`,
 * а у неё свой `Content-Security-Policy`: в списке `img-src` перечислены
 * домены ВК, и нашего там нет — браузер рубит картинку ещё до сети, галерея
 * открывается пустой. Проверено 18.09.2026; заголовками своего nginx это не
 * лечится, разрешить себя чужой странице нечем. Поэтому в вебе показываем
 * снимки сами.
 *
 * Платформу называет `vk_platform` из параметров запуска: `desktop_web` и
 * `mobile_web` — страница в браузере, остальное — клиент. Параметров нет
 * вовсе (дев-режим вне ВК) — тоже свой: мост там не отвечает никогда, и
 * нативная галерея просто не откроется.
 */
function ownViewer(): boolean {
  const platform = new URLSearchParams(launchParams()).get('vk_platform');
  return platform === null || platform.includes('web');
}

/** Открыть набор снимков, начиная с указанного. Пустой набор ничего не делает. */
export function openPhotos(images: string[], startIndex = 0): void {
  const list = images.filter(Boolean);
  if (list.length === 0) return;

  const index = Math.min(Math.max(startIndex, 0), list.length - 1);

  if (!ownViewer()) {
    void showImages(list, index);
    return;
  }

  opened = { images: list, index };
  publish();
}

export function closePhotos(): void {
  opened = null;
  publish();
}

/** Листание по кругу: с последнего снимка вперёд — на первый. */
export function stepPhoto(delta: number): void {
  if (opened === null) return;
  const count = opened.images.length;
  opened = { ...opened, index: (opened.index + delta + count) % count };
  publish();
}

export function subscribePhotos(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function currentPhotos(): PhotoView | null {
  return opened;
}
